import { useEffect, useState } from "react";
import { getOperatorContactIds } from "@/services/operator-contacts.cache";

// Returns the Set of technical-operator contactIds for the current session.
// First render returns an empty set while the request is in flight; subsequent
// renders (across the app) read the cached value synchronously.
export const useOperatorContacts = (): Set<number> => {
  const [ids, setIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    let mounted = true;
    getOperatorContactIds().then((set) => {
      if (mounted) setIds(set);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return ids;
};

// Pure helper to filter out technical-operator participations from any list.
// Works regardless of `participationType` — matches by `contactId` only.
// Accepts both top-level `contactId` and nested `contact.contactId` to cover
// the two payload shapes returned by the API (list views nest contact info,
// detail views expose it flat).
export const filterOutOperators = <
  T extends { contactId?: number | string; contact?: { contactId?: number | string } },
>(
  participations: T[] | undefined | null,
  operatorIds: Set<number>,
): T[] => {
  if (!participations?.length) return [];
  if (!operatorIds.size) return participations;
  return participations.filter((p) => {
    const cid = Number(p?.contactId ?? p?.contact?.contactId ?? 0);
    return !operatorIds.has(cid);
  });
};
